import {
  formatJumpTransitionLabel,
  type Jump,
  type JumpGroup,
  type Level,
} from '@wht-xc-utils/shared/domain';

export interface JumpTransitionTarget {
  jumpId: string;
  label: string;
}

export interface JumpTransition {
  from: JumpTransitionTarget | null;
  to: JumpTransitionTarget | null;
}

export function deriveJumpTransitions(
  jumps: readonly Jump[],
  groups: readonly JumpGroup[],
  levels: readonly Level[],
): Map<string, JumpTransition> {
  const jumpById = new Map(jumps.map((jump) => [jump.id, jump]));
  const levelById = new Map(levels.map((level) => [level.id, level]));
  const transitions = new Map<string, JumpTransition>();

  for (const group of groups) {
    const groupJumps = group.jumpIds
      .map((jumpId) => jumpById.get(jumpId))
      .filter((jump): jump is Jump => !!jump)
      .map((jump) => ({ jump, level: levelById.get(jump.levelId) }))
      .filter((item): item is { jump: Jump; level: Level } => !!item.level);

    for (const current of groupJumps) {
      const currentOrder = current.level.runOrder;
      if (currentOrder == null) continue;
      const previous = groupJumps
        .filter((item) => item.level.runOrder != null && item.level.runOrder < currentOrder)
        .sort((a, b) => (b.level.runOrder ?? 0) - (a.level.runOrder ?? 0))[0];
      const next = groupJumps
        .filter((item) => item.level.runOrder != null && item.level.runOrder > currentOrder)
        .sort((a, b) => (a.level.runOrder ?? 0) - (b.level.runOrder ?? 0))[0];

      transitions.set(current.jump.id, {
        from: previous
          ? {
              jumpId: previous.jump.id,
              label: formatJumpTransitionLabel(previous.level.canonicalSlug, previous.jump.number),
            }
          : null,
        to: next
          ? {
              jumpId: next.jump.id,
              label: formatJumpTransitionLabel(next.level.canonicalSlug, next.jump.number),
            }
          : null,
      });
    }
  }

  return transitions;
}
